import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports, type ReconciliationRow } from '@/api/documents';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DatePopover from '@/components/DatePopover';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const DOC_LABELS: Record<string, string> = {
  'doc.goods_receipts': 'Надходження',
  'doc.goods_sales': 'Реалізація',
  'doc.bank_receipts': 'Банк (надход.)',
  'doc.bank_payments': 'Банк (виплата)',
  'doc.cash_receipts': 'ПКО',
  'doc.cash_payments': 'РКО',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  'doc.debt_adjustments': 'Коригування боргу',
  'doc.additional_expenses': 'Дод. витрати',
};

export default function ReconciliationActPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });

  const [orgId, setOrgId] = useState('');
  const [contractorId, setContractorId] = useState('');
  const [contractId, setContractId] = useState('');
  const [from, setFrom] = useState<string>('2026-01-01');
  const [to, setTo] = useState<string>('2026-12-31');

  if (!orgId && orgList.data?.items[0]) setOrgId(orgList.data.items[0].id);

  const contractsQ = useQuery({
    queryKey: ['contracts', orgId, contractorId],
    queryFn: () => refs.contracts({ organization_id: orgId, contractor_id: contractorId }),
    enabled: !!orgId && !!contractorId,
  });

  const act = useQuery({
    queryKey: ['reconciliation-act', orgId, contractorId, contractId, from, to],
    queryFn: () => reports.reconciliationAct({
      organization_id: orgId,
      contractor_id: contractorId,
      contract_id: contractId || undefined,
      date_from: `${from}T00:00:00+02:00`,
      date_to: `${to}T23:59:59+02:00`,
    }),
    enabled: !!orgId && !!contractorId,
  });

  const cols = useMemo<ColumnDef<ReconciliationRow>[]>(() => [
    {
      key: 'movement_date', header: 'Дата', width: 160,
      sortValue: (r) => r.movement_date ?? '',
      cell: (r) => r.movement_date ? new Date(r.movement_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'document_type', header: 'Документ', width: 200,
      sortValue: (r) => DOC_LABELS[r.document_type] ?? r.document_type,
      cell: (r) => DOC_LABELS[r.document_type] ?? r.document_type,
    },
    {
      key: 'document_number', header: '№', width: 130,
      sortValue: (r) => r.document_number ?? '',
      cell: (r) => r.document_number,
    },
    {
      key: 'debit', header: 'Дебет (на нашу користь)', width: 180, align: 'right',
      sortValue: (r) => Number(r.debit),
      cell: (r) => r.debit && Number(r.debit) > 0 ? fmt(r.debit) : '',
    },
    {
      key: 'credit', header: 'Кредит (наш борг ↓ або вони ↓)', width: 220, align: 'right',
      sortValue: (r) => Number(r.credit),
      cell: (r) => r.credit && Number(r.credit) > 0 ? fmt(r.credit) : '',
    },
    {
      key: 'balance', header: 'Сальдо', width: 140, align: 'right',
      sortValue: (r) => Number(r.balance),
      cell: (r) => {
        const v = Number(r.balance);
        const color = v > 0 ? '#15803d' : v < 0 ? '#b91c1c' : '#000';
        return <span style={{ fontWeight: 600, color }}>{fmt(r.balance)}</span>;
      },
    },
  ], []);

  const balance = Number(act.data?.closing_balance ?? 0);
  const balanceLabel = balance > 0
    ? `Контрагент винен нам ${fmt(balance)} грн`
    : balance < 0
    ? `Ми винні контрагенту ${fmt(-balance)} грн`
    : 'Розрахунки збалансовано (0 грн)';

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Акт звірки розрахунків</h2>
        {act.data && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {Number(act.data.closing_balance) !== 0 && (() => {
              // Підбираємо account_code: для покупця (3xx) → 361, для поста-
              // чальника (6xx) → 631. Якщо знаємо договір — точніше: треба було б
              // подивитися contract_type, але це рішення спрощене.
              const balance = Number(act.data!.closing_balance);
              // Якщо balance > 0 → контрагент винен (актив 361/377).
              //   Логічна операція: WRITE_OFF або зачет.
              // Якщо balance < 0 → ми винні (пасив 631/685).
              //   Логічна операція: WRITE_OFF кредиторки або зачет.
              const debtorAccount = balance > 0 ? '361' : '631';
              const url = new URLSearchParams({
                organization_id: orgId,
                adjustment_type: 'WRITE_OFF',
                debtor_contractor_id: contractorId,
                debtor_contract_id: contractId,
                debtor_account_code: debtorAccount,
                amount: String(Math.abs(balance).toFixed(2)),
              });
              return (
                <button
                  onClick={() => navigate(`/debt-adjustments/new?${url.toString()}`)}
                  title="Створити документ коригування на основі сальдо"
                >
                  ↗ Створити коригування ({Math.abs(balance).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн)
                </button>
              );
            })()}
            <button
              className="primary"
              onClick={() => {
                const url = new URLSearchParams({
                  organization_id: orgId,
                  contractor_id: contractorId,
                  ...(contractId ? { contract_id: contractId } : {}),
                  date_from: from,
                  date_to: to,
                });
                navigate(`/reports/reconciliation-act/print?${url.toString()}`);
              }}
            >
              🖨 Друкувати
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Контрагент</label>
          <select value={contractorId} onChange={(e) => { setContractorId(e.target.value); setContractId(''); }}>
            <option value="">—</option>
            {contractorsQ.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Договір (всі)</label>
          <select value={contractId} onChange={(e) => setContractId(e.target.value)} disabled={!contractorId}>
            <option value="">— всі договори —</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>З</label>
          <DatePopover value={from} onChange={(v) => setFrom(v)} />
        </div>
        <div className="field">
          <label>По</label>
          <DatePopover value={to} onChange={(v) => setTo(v)} />
        </div>
      </div>

      {act.data && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '0.5rem', padding: '0.75rem', background: '#f8fafc',
          borderRadius: 6, marginBottom: '1rem',
        }}>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Сальдо на початок</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{fmt(act.data.opening_balance)}</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Оборот по дебету</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{fmt(act.data.turnover_debit)}</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Оборот по кредиту</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{fmt(act.data.turnover_credit)}</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>Сальдо на кінець</div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem',
              color: balance > 0 ? '#15803d' : balance < 0 ? '#b91c1c' : undefined }}>
              {balanceLabel}
            </div>
          </div>
        </div>
      )}

      <DataTable<ReconciliationRow>
        rows={act.data?.rows ?? []}
        columns={cols}
        rowKey={(r) => `${r.document_id}-${r.movement_date}-${r.direction}`}
        loading={act.isLoading}
        emptyText="Немає рухів за обраний період."
      />
    </>
  );
}
